import { database } from './firebase';
import { ref, push, set, onValue, update, remove } from 'firebase/database';

class MantenimientoService {
  static async crearMantenimiento(datosMantenimiento) {
    try {
      const mantenimientosRef = ref(database, 'mantenimientos');
      const nuevoMantenimientoRef = push(mantenimientosRef);
      const mantenimientoConMeta = {
        ...datosMantenimiento,
        id: nuevoMantenimientoRef.key,
        fechaCreacion: new Date().toISOString(),
        timestamp: Date.now(),
      };
      await set(nuevoMantenimientoRef, mantenimientoConMeta);
      console.log('Mantenimiento creado con ID:', nuevoMantenimientoRef.key);
      return mantenimientoConMeta;
    } catch (error) {
      console.error('Error al crear mantenimiento:', error);
      throw error;
    }
  }

  static async actualizarMantenimiento(mantenimientoId, datosActualizacion) {
    try {
      const mantenimientoRef = ref(database, `mantenimientos/${mantenimientoId}`);
      const datosActualizados = {
        ...datosActualizacion,
        fechaActualizacion: new Date().toISOString(),
        timestamp: Date.now(),
      };
      await update(mantenimientoRef, datosActualizados);
      console.log('Mantenimiento actualizado:', mantenimientoId);
      return datosActualizados;
    } catch (error) {
      console.error('Error al actualizar mantenimiento:', error);
      throw error;
    }
  }

  static async eliminarMantenimiento(mantenimientoId) {
    try {
      const mantenimientoRef = ref(database, `mantenimientos/${mantenimientoId}`);
      await remove(mantenimientoRef);
      console.log('Mantenimiento eliminado:', mantenimientoId);
    } catch (error) {
      console.error('Error al eliminar mantenimiento:', error);
      throw error;
    }
  }

  static suscribirseMantenimientos(callback) {
    const mantenimientosRef = ref(database, 'mantenimientos');
    return onValue(mantenimientosRef, (snapshot) => {
      console.log('Snapshot de mantenimientos recibido:', snapshot.val());
      const mantenimientos = [];
      if (snapshot.exists()) {
        snapshot.forEach((child) => {
          console.log('Mantenimiento:', child.key, child.val());
          mantenimientos.push(child.val());
        });
      }
      console.log('Mantenimientos procesados:', mantenimientos);
      callback(mantenimientos);
    }, (error) => {
      console.error('Error al suscribirse a mantenimientos:', error);
    });
  }

  static async obtenerMantenimientosPorAuto(autoId) {
    try {
      return new Promise((resolve, reject) => {
        const mantenimientosRef = ref(database, 'mantenimientos');
        onValue(mantenimientosRef, (snapshot) => {
          const mantenimientos = [];
          if (snapshot.exists()) {
            snapshot.forEach((child) => {
              const mantenimiento = child.val();
              if (mantenimiento.autoId === autoId) {
                mantenimientos.push(mantenimiento);
              }
            });
          }
          resolve(mantenimientos);
        }, reject);
      });
    } catch (error) {
      console.error('Error al obtener mantenimientos por auto:', error);
      throw error;
    }
  }

  static async obtenerMantenimientosPorRangoFechas(fechaInicio, fechaFin) {
    try {
      return new Promise((resolve, reject) => {
        const mantenimientosRef = ref(database, 'mantenimientos');
        onValue(mantenimientosRef, (snapshot) => {
          const mantenimientos = [];
          if (snapshot.exists()) {
            snapshot.forEach((child) => {
              const mantenimiento = child.val();
              if (mantenimiento.fecha >= fechaInicio && mantenimiento.fecha <= fechaFin) {
                mantenimientos.push(mantenimiento);
              }
            });
          }
          resolve(mantenimientos);
        }, reject);
      });
    } catch (error) {
      console.error('Error al obtener mantenimientos por rango de fechas:', error);
      throw error;
    }
  }

  static calcularEstadisticas(mantenimientos) {
    if (mantenimientos.length === 0) {
      return {
        totalMantenimientos: 0,
        montoTotal: 0,
        mantenimientosPreventivos: 0,
        mantenimientosCorrectivos: 0,
        mantenimientosEmergencia: 0,
        promedioMonto: 0,
        frecuenciaMantenimiento: 0
      };
    }

    const montoTotal = mantenimientos.reduce((sum, m) => sum + (parseFloat(m.monto) || 0), 0);
    const mantenimientosPreventivos = mantenimientos.filter(m => m.tipoMantenimiento === 'preventivo').length;
    const mantenimientosCorrectivos = mantenimientos.filter(m => m.tipoMantenimiento === 'correctivo').length;
    const mantenimientosEmergencia = mantenimientos.filter(m => m.tipoMantenimiento === 'emergencia').length;
    const promedioMonto = montoTotal / mantenimientos.length;

    // Calcular frecuencia de mantenimiento (mantenimientos por mes)
    const fechas = mantenimientos.map(m => new Date(m.fecha)).sort((a, b) => a - b);
    const diasDiferencia = fechas.length > 1 ? 
      (fechas[fechas.length - 1] - fechas[0]) / (1000 * 60 * 60 * 24) : 0;
    const frecuenciaMantenimiento = diasDiferencia > 0 ? 
      (mantenimientos.length / (diasDiferencia / 30)) : 0;

    return {
      totalMantenimientos: mantenimientos.length,
      montoTotal: Math.round(montoTotal * 100) / 100,
      mantenimientosPreventivos,
      mantenimientosCorrectivos,
      mantenimientosEmergencia,
      promedioMonto: Math.round(promedioMonto * 100) / 100,
      frecuenciaMantenimiento: Math.round(frecuenciaMantenimiento * 100) / 100
    };
  }

  static generarAlertasPredictivas(autos, mantenimientos, consumos) {
    const alertas = [];
    
    autos.forEach(auto => {
      // Obtener mantenimientos del auto
      const mantenimientosAuto = mantenimientos.filter(m => m.autoId === auto.id);
      const ultimoMantenimiento = mantenimientosAuto
        .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0];

      // Obtener consumos del auto
      const consumosAuto = consumos.filter(c => c.vehiculo.includes(auto.placas));
      
      // Análisis de consumo de combustible
      if (consumosAuto.length >= 3) {
        const rendimientos = consumosAuto.map(c => c.rendimiento || 0);
        const rendimientoPromedio = rendimientos.reduce((a, b) => a + b, 0) / rendimientos.length;
        const ultimoRendimiento = rendimientos[rendimientos.length - 1];
        
        if (ultimoRendimiento < rendimientoPromedio * 0.85) {
          alertas.push({
            tipo: 'warning',
            auto: `${auto.placas} - ${auto.marca} ${auto.modelo}`,
            mensaje: `El vehículo ${auto.placas} muestra consumo anormal de combustible (${ultimoRendimiento.toFixed(1)} km/l vs promedio ${rendimientoPromedio.toFixed(1)} km/l). Posible problema en inyectores. Revisión sugerida en 5 días.`,
            prioridad: 'alta',
            icono: '⚠️'
          });
        }
      }

      // Análisis de frecuencia de mantenimiento
      if (mantenimientosAuto.length >= 2) {
        const fechasMantenimiento = mantenimientosAuto
          .map(m => new Date(m.fecha))
          .sort((a, b) => b - a);
        
        const diasDesdeUltimoMantenimiento = Math.floor(
          (new Date() - fechasMantenimiento[0]) / (1000 * 60 * 60 * 24)
        );

        // Si han pasado más de 90 días desde el último mantenimiento
        if (diasDesdeUltimoMantenimiento > 90) {
          alertas.push({
            tipo: 'info',
            auto: `${auto.placas} - ${auto.marca} ${auto.modelo}`,
            mensaje: `El vehículo ${auto.placas} no ha tenido mantenimiento en ${diasDesdeUltimoMantenimiento} días. Se recomienda revisión preventiva.`,
            prioridad: 'media',
            icono: '🔧'
          });
        }
      }

      // Análisis de patrones de mantenimiento
      const mantenimientosPreventivos = mantenimientosAuto.filter(m => m.tipoMantenimiento === 'preventivo');
      const mantenimientosCorrectivos = mantenimientosAuto.filter(m => m.tipoMantenimiento === 'correctivo');
      
      if (mantenimientosCorrectivos.length > mantenimientosPreventivos.length) {
        alertas.push({
          tipo: 'error',
          auto: `${auto.placas} - ${auto.marca} ${auto.modelo}`,
          mensaje: `El vehículo ${auto.placas} tiene más mantenimientos correctivos (${mantenimientosCorrectivos.length}) que preventivos (${mantenimientosPreventivos.length}). Se recomienda implementar un programa de mantenimiento preventivo.`,
          prioridad: 'alta',
          icono: '🚨'
        });
      }
    });

    return alertas;
  }

  static async eliminarTodosLosRegistros() {
    try {
      const mantenimientosRef = ref(database, 'mantenimientos');
      await remove(mantenimientosRef);
      console.log('Todos los registros de mantenimiento han sido eliminados exitosamente');
      return true;
    } catch (error) {
      console.error('Error al eliminar todos los registros de mantenimiento:', error);
      throw error;
    }
  }
}

export { MantenimientoService };









